import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Save, Database, FlaskRound } from "lucide-react";
import { toast } from "sonner";

export default function Parametros() {
  const [params, setParams] = useState(null);
  const [form, setForm] = useState({});
  const [inputs, setInputs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/parameters");
    setParams(data);
    setForm({
      milk_density_kg_per_l: data.milk_density_kg_per_l,
      curd_yield_pct: data.curd_yield_pct,
      default_correction_cost: data.default_correction_cost,
      kg_per_tambo: data.kg_per_tambo,
      tambos_per_tarima: data.tambos_per_tarima,
    });
    setInputs(data.correction_inputs || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/parameters", {
        milk_density_kg_per_l: parseFloat(form.milk_density_kg_per_l),
        curd_yield_pct: parseFloat(form.curd_yield_pct),
        default_correction_cost: parseFloat(form.default_correction_cost),
        kg_per_tambo: parseFloat(form.kg_per_tambo),
        tambos_per_tarima: parseInt(form.tambos_per_tarima),
      });
      toast.success("Parámetros actualizados");
      load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const saveInputs = async () => {
    setSaving(true);
    try {
      await api.put("/parameters", { correction_inputs: inputs });
      toast.success("Precios de insumos actualizados");
      load();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const updateInputPrice = (idx, val) => {
    const next = [...inputs];
    next[idx] = { ...next[idx], price_per_kg: parseFloat(val) || 0 };
    setInputs(next);
  };

  if (!params) return <div className="p-8 text-stone-500">Cargando…</div>;

  return (
    <div className="max-w-screen-xl mx-auto p-6 lg:p-8 space-y-6" data-testid="parametros-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 flex items-center gap-2">
          <Settings className="w-7 h-7 text-[#1E3A8A]" strokeWidth={1.75} /> Parámetros del Sistema
        </h1>
        <p className="text-sm text-stone-500 mt-1">Configura constantes, precios de insumos y tablas usadas en todos los cálculos del ERP.</p>
      </div>

      {/* CONSTANTES */}
      <Card className="p-6 border-stone-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-4 h-4 text-stone-600" />
          <h3 className="font-outfit font-semibold text-lg text-stone-900">Constantes operativas</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <FieldEdit label="Densidad de la leche (g/mL)" v={form.milk_density_kg_per_l} on={(v) => setForm({ ...form, milk_density_kg_per_l: v })} step="0.001" help="Usado en conversión litros ↔ kilos." testid="param-density" />
          <FieldEdit label="Rendimiento estándar de cuajada (%)" v={form.curd_yield_pct} on={(v) => setForm({ ...form, curd_yield_pct: v })} step="0.1" help="Default para la calculadora IA." testid="param-yield" />
          <FieldEdit label="Costo corrección default ($)" v={form.default_correction_cost} on={(v) => setForm({ ...form, default_correction_cost: v })} step="100" help="Usado cuando no hay historial." />
          <FieldEdit label="Kg por tambo" v={form.kg_per_tambo} on={(v) => setForm({ ...form, kg_per_tambo: v })} step="1" />
          <FieldEdit label="Tambos por tarima" v={form.tambos_per_tarima} on={(v) => setForm({ ...form, tambos_per_tarima: v })} step="1" />
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="param-save-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Guardando..." : "Guardar constantes"}
          </Button>
        </div>
      </Card>

      {/* INSUMOS DE CORRECCIÓN */}
      <Card className="border-stone-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-2">
          <FlaskRound className="w-4 h-4 text-stone-600" />
          <h3 className="font-outfit font-semibold text-lg text-stone-900">Insumos de corrección</h3>
          <span className="text-xs text-stone-500 ml-2">Edita el precio por kg. Se usa automáticamente al registrar correcciones.</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="insumos-table">
            <thead>
              <tr className="bg-stone-100 border-b border-stone-200">
                {["Insumo", "Función principal", "Comentario técnico", "Precio ($/kg)"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inputs.map((it, i) => (
                <tr key={it.code} className="border-b border-stone-100 hover:bg-stone-50/50">
                  <td className="px-4 py-3 text-sm font-medium text-stone-900">{it.name}</td>
                  <td className="px-4 py-3 text-sm text-stone-700">{it.function}</td>
                  <td className="px-4 py-3 text-xs text-stone-500 max-w-md">{it.technical}</td>
                  <td className="px-4 py-3">
                    <Input
                      type="number"
                      step="0.01"
                      value={it.price_per_kg}
                      onChange={(e) => updateInputPrice(i, e.target.value)}
                      className="w-28 tabular-nums"
                      data-testid={`insumo-price-${it.code}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-stone-200 flex justify-end">
          <Button onClick={saveInputs} disabled={saving} data-testid="insumos-save-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
            <Save className="w-4 h-4 mr-1.5" /> Guardar precios de insumos
          </Button>
        </div>
      </Card>

      {/* TABLA pH */}
      <Card className="p-6 border-stone-200 bg-white shadow-sm">
        <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-1">Tabla pH → Acidez Dornic</h3>
        <p className="text-xs text-stone-500 mb-4">Aplicada automáticamente en Recepción, Laboratorio y Calculadora IA.</p>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="ph-table">
            <thead>
              <tr className="bg-stone-100 border-b border-stone-200">
                {["pH (rango)", "Dornic (°D)", "Estado de la cuajada"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {params.ph_dornic_table.map((row, i) => (
                <tr key={i} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3 text-sm tabular-nums font-medium text-stone-900">{row.ph_min} – {row.ph_max}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-[#1E3A8A] font-medium">{row.dornic_min} – {row.dornic_max} °D</td>
                  <td className="px-4 py-3 text-sm text-stone-700">{row.estado_cuajada}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* TABLA PROTEÍNA */}
      <Card className="p-6 border-stone-200 bg-white shadow-sm">
        <h3 className="font-outfit font-semibold text-lg text-stone-900 mb-1">Tabla de calidad por % Proteína</h3>
        <p className="text-xs text-stone-500 mb-4">Aplicada automáticamente al capturar proteína en Recepción.</p>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="protein-table">
            <thead>
              <tr className="bg-stone-100 border-b border-stone-200">
                {["Proteína (%)", "Calidad", "Semáforo", "Impacto esperado"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-outfit font-semibold uppercase tracking-wider text-stone-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { range: "> 3.40", label: "Excelente", color: "bg-emerald-500", colorTxt: "Verde", impact: "Alto rendimiento quesero" },
                { range: "3.20 – 3.39", label: "Buena", color: "bg-emerald-500", colorTxt: "Verde", impact: "Rendimiento normal" },
                { range: "3.00 – 3.19", label: "Aceptable", color: "bg-amber-500", colorTxt: "Amarillo", impact: "Rendimiento ligeramente reducido" },
                { range: "2.80 – 2.99", label: "Baja", color: "bg-orange-500", colorTxt: "Naranja", impact: "Menor rendimiento y firmeza" },
                { range: "< 2.80", label: "Deficiente", color: "bg-red-500", colorTxt: "Rojo", impact: "Posible adulteración, mala alimentación o problemas sanitarios" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3 text-sm tabular-nums font-medium text-stone-900">{row.range}</td>
                  <td className="px-4 py-3 text-sm text-stone-700">{row.label}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><span className={`w-3 h-3 rounded-full ${row.color}`}></span><span className="text-sm text-stone-700">{row.colorTxt}</span></div></td>
                  <td className="px-4 py-3 text-sm text-stone-700">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="text-xs text-stone-400 text-center">Última actualización: {new Date(params.updated_at).toLocaleString("es-MX")}</div>
    </div>
  );
}

const FieldEdit = ({ label, v, on, step, help, testid }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600">{label}</Label>
    <Input type="number" step={step} value={v} onChange={(e) => on(e.target.value)} className="mt-1.5" data-testid={testid} />
    {help && <p className="text-[11px] text-stone-500 mt-1">{help}</p>}
  </div>
);
